import React, { useMemo, useState } from 'react';
import { Table } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import TableBody from './tableBody';
import TableHead from './tableHead';
import ConfirmModal from '../../../../components/Modal/Confirm';
import ConfirmTemplates from '../ConfirmTemplates';

import { getUsersSASignin } from '../../../../services/endpoints/users';
import { onChangeUserStatus, getActivityLogAction, getUserDataAction, getSignInHistoryAction } from '../../../../redux/actions/users/actions';

import { submitMassAction, prepareConfirmBtnsText } from '../../helpers';
import useShiftSelected from '../../../../utils/hooks/useShiftSelected';

const TableUsers = ({
  isLoading,
  filters,
  access,
  users,
  accessMassActions,
  statuses,
  levels,
  values,
  setValues,
  inc,
  fields,
  columns,
  openModal,
  setUsers,
  config,
  historySearch,
  onSearchByIPs,
}) => {
  const dispatch = useDispatch();

  const actionFetching = useSelector((state) => state.users.actionFetching);

  const usersIds = useMemo(() => users?.filter((u) => u.status !== 4).map((u) => u.id), [users]);

  const [isLoadingItems, setIsLoadingItems] = useState([]);
  const [isOpenConfirm, setIsOpen] = useState(false);
  const [confirmValues, setConfirmValues] = useState({
    key: null,
    title: '',
    id: 0,
  });
  const toggleConfirm = () => setIsOpen(!isOpenConfirm);

  const onChange = useShiftSelected(usersIds, values);

  const onChangeCheck = (event, user) => {
    const nextValue = onChange(event, user.id);

    setValues(nextValue);
    setUsers(users.filter((u) => nextValue.includes(u.id)));
  };

  const onChangeCheckAll = (e) => {
    if (e.target.checked) {
      setUsers([...users.filter((item) => item.status !== 4)]);
      return setValues([...users.filter((item) => item.status !== 4).map((item) => item.id)]);
    }
    if (!e.target.checked) {
      setUsers([]);
      return setValues([]);
    }
  };

  const getSigninSALink = async (id) => {
    setIsLoadingItems((prev) => [...prev, id]);
    try {
      const response = await getUsersSASignin(id);
      window.open(response.data.url, '_blank');
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoadingItems((prev) => prev?.filter((p) => p !== id));
    }
  };

  const handleSelect = (key, title, item, actionType) => {
    if (title === 'Sign in as user') {
      getSigninSALink(item.id);
      return;
    }

    if (title === 'Copy rates') {
      return openModal(3, item);
    }
    if (title === 'Set password') {
      return openModal(0, item);
    }
    if (title === 'Copy rates from user') {
      return openModal(4);
    }
    if (title === 'Activity log') {
      dispatch(getActivityLogAction(item.id));
      return openModal(5, item);
    }
    if (title === 'Edit user') {
      dispatch(getUserDataAction(item.id));
      return openModal(2, item);
    }
    if (title === 'Sign-in history') {
      dispatch(getSignInHistoryAction(item.id, { query: historySearch }));
      return openModal(6, item);
    }
    if (title === 'Show users with same IPs') {
      return onSearchByIPs?.(item.id);
    }
    if (title === 'Order refunds') {
      return openModal(8, item);
    }

    if (item) {
      setConfirmValues({
        key,
        title,
        id: item.id,
        actionType,
        item,
      });
    } else {
      setConfirmValues({
        key,
        title,
        actionType,
        selectedCount: values.length,
      });
    }
    toggleConfirm();
  };

  const onSubmitConfirm = async () => {
    try {
      switch (confirmValues.title) {
      case 'Suspend user': {
        await dispatch(onChangeUserStatus(confirmValues.id, 1));
        break;
      }
      case 'Activate user': {
        await dispatch(onChangeUserStatus(confirmValues.id, 0));
        break;
      }
      default: {
        await dispatch(
          submitMassAction({
            ...confirmValues,
            values,
            openModal,
            setUsers,
          }),
        );
      }
      }
      inc();
      setValues([]);
      toggleConfirm();
    } catch (err) {
      toggleConfirm();
    }
  };

  return (
    <Table>
      <ConfirmModal
        toggle={toggleConfirm}
        isOpen={isOpenConfirm}
        onConfirm={onSubmitConfirm}
        fetching={actionFetching}
        title={<ConfirmTemplates.Title {...confirmValues} />}
        description={<ConfirmTemplates.Body {...confirmValues} />}
        {...prepareConfirmBtnsText(confirmValues.title, confirmValues.actionType)}
      />
      <TableHead
        filters={filters}
        handleSelect={handleSelect}
        values={values}
        access={access}
        onChangeCheck={onChangeCheckAll}
        accessMassActions={accessMassActions}
        levels={levels}
        statuses={statuses}
        config={config}
        columns={columns}
        fields={fields}
      />
      <TableBody
        isLoading={isLoading}
        isLoadingItems={isLoadingItems}
        users={users}
        access={access}
        values={values}
        handleSelect={handleSelect}
        onChangeCheck={onChangeCheck}
        openModal={openModal}
        config={config}
        fields={fields}
      />
    </Table>
  );
};

TableUsers.propTypes = {
  isLoading: PropTypes.bool,
  access: PropTypes.object,
  filters: PropTypes.object,
  users: PropTypes.array,
  accessMassActions: PropTypes.array,
  levels: PropTypes.array,
  statuses: PropTypes.array,
  values: PropTypes.array,
  setValues: PropTypes.func,
  fields: PropTypes.array,
  columns: PropTypes.array,
  inc: PropTypes.func,
  openModal: PropTypes.func,
  setUsers: PropTypes.func,
  config: PropTypes.object,
  historySearch: PropTypes.string,
  onSearchByIPs: PropTypes.func,
};

export default TableUsers;
